import {
	Download as DownloadIcon,
	InfoOutlined as InfoOutlinedIcon,
	SystemUpdateAlt as SystemUpdateAltIcon,
	Timelapse as TimelapseIcon
} from "@mui/icons-material";
import { Box, CircularProgress, Grid, IconButton, Typography } from "@mui/material";
import { DataGrid, GridColDef, GridPagination, GridRenderCellParams } from "@mui/x-data-grid";
import { sortBy } from "lodash";
import moment from "moment";
import { FC, useEffect, useMemo, useState } from "react";

import DeviceSelection from "../../components/DeviceSelection";
import TablePagination from "../../components/TablePagination";
import TimeRangeSelection from "../../components/TimeRangeSelection";
import { useDownloadJob } from "../../hooks/useDownloadReport";
import { useAppSelector } from "../../redux";
import { useGetAllDevicesListQuery } from "../../redux/reducers/settings.reducer";
import { useGetUptimeMetricsQuery } from "../../redux/reducers/uptime.reducer";
import {
	IGetUptimeMetricsRequestData,
	IUptimeCardDetails,
	IUptimeHistoryDetails,
	IUptimeRowDetails,
	UptimeCardValueType
} from "../../types";
import { getFormattedDuration } from "../../utils/commonUtils";
import { API_TIMESTAMP_FORMAT, PAGINATION_CONSTANTS } from "../../utils/constants";
import DowntimeEventInfoDialog from "./DowntimeEventInfoDialog";
import "./uptime.css";

const UPTIME_CARDS_LIST: IUptimeCardDetails[] = [
	{
		type: "uptime",
		icon: <SystemUpdateAltIcon color="inherit" className="rotate" />,
		name: "Total Uptime",
		value: "percent_uptime"
	},
	{
		type: "uptime",
		icon: <TimelapseIcon color="inherit" />,
		name: "Total Uptime Duration",
		value: "uptime_seconds"
	},
	{
		type: "downtime",
		icon: <SystemUpdateAltIcon color="inherit" />,
		name: "Total Downtime",
		value: "percent_downtime"
	},
	{
		type: "downtime",
		icon: <TimelapseIcon color="inherit" />,
		name: "Total Downtime Duration",
		value: "downtime_seconds"
	}
];

const UptimeListPagination = (props: any) => (
	<GridPagination
		{...props}
		ActionsComponent={TablePagination}
		labelDisplayedRows={() => <></>}
		classes={{ spacer: "grid-pagination-spacer-custom" }}
	/>
);

const Uptime: FC = () => {
	// STORE
	const filtersState = useAppSelector((state) => state.filters);
	const { timeFilter } = filtersState;

	const { isLoading: showDownloadButtonLoader, startDownload } = useDownloadJob();

	// STATES
	const [selectedUptimeEvent, setSelectedUptimeEvent] = useState<IUptimeRowDetails | null>(null);
	const [filters, setFilters] = useState<IGetUptimeMetricsRequestData>({
		start_time: moment().startOf("day").format(API_TIMESTAMP_FORMAT.TIME_FILTER),
		end_time: moment().endOf("day").format(API_TIMESTAMP_FORMAT.TIME_FILTER)
	});

	// APIS
	// GET ALL DEVICES LIST
	const { data: getDevicesListResponse } = useGetAllDevicesListQuery();

	const uptimeFilters = useMemo<IGetUptimeMetricsRequestData>(() => {
		if (!filters.device_id && getDevicesListResponse && getDevicesListResponse.length > 0) {
			return {
				...filters,
				device_id: getDevicesListResponse[0].id
			};
		}

		return filters;
	}, [filters, getDevicesListResponse]);

	const { data: uptimeMetricsResponse } = useGetUptimeMetricsQuery(uptimeFilters, {
		skip: typeof uptimeFilters.device_id !== "number"
	});

	const rowsData = useMemo<IUptimeRowDetails[]>(() => {
		if (uptimeMetricsResponse && uptimeMetricsResponse.data && typeof uptimeFilters.device_id === "number") {
			const sortedUptimeMetrics = sortBy(uptimeMetricsResponse.data, "date");

			return sortedUptimeMetrics.map((uptimeMetricsItem, index) => {
				const sortedNestedData = sortBy(uptimeMetricsItem.nested_data, "start_time");

				const uptimeHistory: IUptimeHistoryDetails[] = sortedNestedData.map((nestedDataItem) => ({
					status: nestedDataItem.is_online_data,
					from: nestedDataItem.start_time,
					to: nestedDataItem.end_time,
					duration: nestedDataItem.duration
				}));

				return {
					id: index,
					sr_no: index + 1,
					date: uptimeMetricsItem.date,
					uptime: getFormattedDuration(Number(uptimeMetricsItem.uptime)),
					downtime: getFormattedDuration(Number(uptimeMetricsItem.downtime)),
					history: uptimeHistory
				};
			});
		}

		return [];
	}, [uptimeFilters.device_id, uptimeMetricsResponse]);

	function handleOpenDowntimeEventInfoDialog(eventInfo: IUptimeRowDetails) {
		setSelectedUptimeEvent(eventInfo);
	}

	function handleCloseDowntimeEventInfoDialog() {
		setSelectedUptimeEvent(null);
	}

	function handleChangeDateValues(from: string, to: string) {
		setFilters((currentFiltersState) => ({
			...currentFiltersState,
			start_time: from,
			end_time: to
		}));
	}

	function handleChangeDevice(deviceIds: number[], societyIds?: number[]) {
		setFilters((currentFiltersState) => {
			const updatedFilters = { ...currentFiltersState };

			if (deviceIds.length >= 0) updatedFilters.device_id = deviceIds[0];
			else delete updatedFilters.device_id;

			if (societyIds && societyIds.length > 0) updatedFilters.society = societyIds;
			else delete updatedFilters.society;

			return updatedFilters;
		});
	}

	function getUptimeMetricsValue(key: UptimeCardValueType): string {
		if (!uptimeMetricsResponse || typeof uptimeFilters.device_id !== "number") return "--";

		switch (key) {
			case "percent_uptime":
				// return `${uptimeMetricsResponse.percent_uptime.toFixed(0)}%`;
				return `${Math.round(uptimeMetricsResponse.percent_uptime * 100) / 100}%`;

			case "uptime_seconds":
				return getFormattedDuration(uptimeMetricsResponse.uptime_seconds);

			case "percent_downtime":
				// return `${uptimeMetricsResponse.percent_downtime.toFixed(0)}%`;
				return `${Math.round(uptimeMetricsResponse.percent_downtime * 100) / 100}%`;

			case "downtime_seconds":
				return getFormattedDuration(uptimeMetricsResponse.downtime_seconds);

			default:
				return "--";
		}
	}

	function handleDownloadButtonClick() {
		startDownload({
			report_type: "uptime",
			job_meta_data: {
				filters: {
					start_date: filters.start_time,
					end_date: moment(filters.end_time, API_TIMESTAMP_FORMAT.TIME_FILTER).isAfter(moment())
						? moment().format(API_TIMESTAMP_FORMAT.TIME_FILTER)
						: filters.end_time,
					device_ids: filters.device_id ? [filters.device_id] : undefined,
					society_ids: filters.society
				}
			}
		});
	}

	const columnsData: GridColDef[] = [
		{
			field: "sr_no",
			headerName: "Sr. No.",
			width: 100,
			sortable: false,
			align: "center",
			headerAlign: "center"
		},
		{
			field: "date",
			headerName: "Date",
			minWidth: 300,
			flex: 2,
			sortable: false,
			align: "center",
			headerAlign: "center",
			valueFormatter: (params) => moment(params.value).format("DD/MM/YYYY")
		},
		{
			field: "uptime",
			headerName: "Uptime",
			minWidth: 200,
			flex: 1,
			sortable: false,
			align: "center",
			headerAlign: "center"
		},
		{
			field: "downtime",
			headerName: "Downtime",
			minWidth: 200,
			flex: 1,
			sortable: false,
			align: "center",
			headerAlign: "center"
		},
		{
			field: "info",
			headerName: "",
			width: 50,
			sortable: false,
			renderCell: (params: GridRenderCellParams<IUptimeRowDetails>) => (
				<Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
					<IconButton size="small" onClick={() => handleOpenDowntimeEventInfoDialog(params.row)}>
						<InfoOutlinedIcon fontSize="inherit" sx={{ color: "var(--color-info-dark)" }} />
					</IconButton>
				</Box>
			)
		}
	];

	useEffect(() => {
		if (timeFilter && timeFilter.start_time && timeFilter.end_time) {
			handleChangeDateValues(timeFilter.start_time, timeFilter.end_time);
		}
	}, [timeFilter]);

	return (
		<Box className="uptime-screen-wrapper">
			<Box className="uptime-content-section">
				<Box className="uptime-filters-section">
					<DeviceSelection disableSelectAllSocieties disableSelectAllDevices onChange={handleChangeDevice} />

					<Box className="uptime-actions-wrapper">
						<TimeRangeSelection customOnly />

						<Box className="uptime-download-button" onClick={handleDownloadButtonClick}>
							{showDownloadButtonLoader ? (
								<CircularProgress color="inherit" size={20} />
							) : (
								<DownloadIcon color="inherit" fontSize="small" />
							)}
						</Box>
					</Box>
				</Box>

				<Box className="uptime-metrics-section">
					<Grid container spacing={{ xs: 1, md: 2 }}>
						{UPTIME_CARDS_LIST.map((uptimeCardItem, index) => (
							<Grid item xs={6} xl={3} key={index}>
								<Box className="uptime-metrics-card">
									<Box
										className={`uptime-metrics-card-icon ${
											uptimeCardItem.type === "uptime" ? "success" : uptimeCardItem.type === "downtime" ? "error" : ""
										}`}
									>
										{uptimeCardItem.icon}
									</Box>

									<Box className="uptime-metrics-card-details">
										<Typography
											variant="body2"
											fontWeight={500}
											color="#666"
											textOverflow="ellipsis"
											whiteSpace="nowrap"
											overflow="hidden"
											sx={{ fontSize: { xs: "0.75rem", sm: "0.8125rem" } }}
										>
											{uptimeCardItem.name}
										</Typography>

										<Typography fontWeight={600} color="#2D2D2D" sx={{ fontSize: { xs: "1rem", sm: "1.125rem" } }}>
											{getUptimeMetricsValue(uptimeCardItem.value)}
										</Typography>
									</Box>
								</Box>
							</Grid>
						))}
					</Grid>
				</Box>

				<Box className="uptime-table-wrapper">
					<DataGrid
						columns={columnsData}
						rows={rowsData}
						disableColumnMenu
						disableRowSelectionOnClick
						density="compact"
						rowSpacingType="border"
						columnHeaderHeight={80}
						pageSizeOptions={[PAGINATION_CONSTANTS.ROWS_PER_PAGE]}
						slots={{ pagination: UptimeListPagination }}
						slotProps={{ footer: { sx: { justifyContent: "center" } } }}
						hideFooter={(uptimeMetricsResponse?.data?.length ?? 0) <= PAGINATION_CONSTANTS.ROWS_PER_PAGE}
						classes={{
							root: "uptime-table-root",
							columnHeader: "uptime-table-column-header",
							columnSeparator: "uptime-table-column-separator",
							cell: "uptime-table-cell",
							columnHeaders: "uptime-table-column-headers"
						}}
					/>
				</Box>
			</Box>

			<DowntimeEventInfoDialog
				open={!!selectedUptimeEvent}
				eventDetails={selectedUptimeEvent}
				onClose={handleCloseDowntimeEventInfoDialog}
			/>
		</Box>
	);
};

export default Uptime;
